import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box } from '@spark-web/box';
import { Spinner } from '@spark-web/spinner';
import { IncomeVerificationState } from './IncomeVerificationOptions';
import { useBankStatement } from './hooks';

type BankVerificationProps = {
  name: string;
  closeDialog: () => void;
  setCurrentState: (state: IncomeVerificationState) => void;
  setIsBankError: (isError: boolean) => void;
};

function BankStatementVerification({ name, closeDialog, setCurrentState, setIsBankError }: BankVerificationProps) {
  const { setValue, watch } = useFormContext();
  const tokenName = `${name}.bankStatementToken`;
  const tokenComplete = `${name}.bankStatementComplete`;
  const [formBankToken, isComplete] = watch([tokenName, tokenComplete]);
  const [bankStatementToken, setBankStatementToken] = useState<string>(formBankToken);

  useEffect(() => {
    if (isComplete) setBankStatementToken('');
  }, [isComplete]);

  const { iframeUrl, isLoading } = useBankStatement({
    bankToken: bankStatementToken,
    onSuccessCallback: id => {
      if (!formBankToken) {
        setValue(tokenName, id);
      }

      setBankStatementToken(id);
    },
    onComplete: () => {
      setValue(tokenComplete, true);
      setValue(tokenName, bankStatementToken);
      closeDialog();
    },
    onError: () => {
      setCurrentState(IncomeVerificationState.UPLOAD_DOCUMENTS);
      setIsBankError(true);
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" flex={1} data-testid="iframe-loader">
        <Spinner size="xsmall" tone="neutral" />
      </Box>
    );
  }

  return (
    <iframe
      src={iframeUrl}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        padding: 0,
      }}
      data-testid="bank-statement-iframe"
    />
  );
}

export default BankStatementVerification;
